import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { isControlRole } from "../../components/control/board-logic";
import { MissionControl } from "../../components/control/mission-control";
import { BySportCard } from "../../components/dashboard/by-sport-card";
import "../../components/dashboard/dashboard.css";
import { FooterBand } from "../../components/dashboard/footer-band";
import { DashboardHero } from "../../components/dashboard/hero";
import { KpiTiles } from "../../components/dashboard/kpi-tiles";
import { MeetRowsCard } from "../../components/dashboard/meet-rows-card";
import { ProgressStrip } from "../../components/dashboard/progress-strip";
import { DASHBOARD_POLL_MS } from "../../components/dashboard/queries";
import { QuickAccess } from "../../components/dashboard/quick-access";
import { groupTotals } from "../../components/dashboard/status-groups";
import { TallyCard } from "../../components/dashboard/tally-card";
import { Button } from "../../components/ui/button";
import { EmptyState } from "../../components/ui/empty-state";
import { errorMessage } from "../../lib/api/messages";
import { type CurrentGamesOption, useCurrentGames } from "../../lib/games";
import { formatInteger } from "../../lib/i18n/format";
import { statusCountsQuery } from "../../lib/meet";

/**
 * Dashboard — the home page, in GMS's layout (user, 2026-10-22): the welcome
 * band (name, active role, where today sits in the games), four KPI tiles,
 * quick access, then the work: what is live, what waits to be made
 * official, every sport's progress, the top of the medal tally — and GMS's
 * footer band. "Today at my venue" is gone; its timeline was one venue's
 * slice of Meets › All sports, which the tiles and quick access open.
 *
 * Every number is counted by the API (Rule 0): ONE status-count read feeds
 * the tiles, the quick-access badges and the progress strip; one grouped
 * read for the sports, one page per list. The page polls every 30 s while
 * visible and never refetches on focus.
 *
 * The games' administrators (SUPER_ADMIN, ADMIN — by the ACTIVE role) get
 * mission control under the tiles (`components/control`): every venue at
 * once, the alerts, the activity log and the inspector.
 */
export const Route = createFileRoute("/_app/")({
	component: DashboardPage,
});

function DashboardPage() {
	const { t } = useTranslation("dashboard");
	const { session, readOnly } = Route.useRouteContext();
	const current = useCurrentGames();
	const games = current.games;
	const gamesId = games?.games_id ?? "";
	const counts = useQuery({ ...statusCountsQuery({ games_id: gamesId }), enabled: games !== undefined, refetchInterval: DASHBOARD_POLL_MS });
	const totals = counts.data ? groupTotals(counts.data) : undefined;

	return (
		<div className="flex flex-col">
			<DashboardHero session={session} games={games} />

			{current.error ? (
				<EmptyState title={t("errors.gamesTitle")} body={errorMessage(current.error)} />
			) : !current.isLoading && !games ? (
				<EmptyState title={t("noGames.title")} body={t("noGames.body")} />
			) : (
				<>
					{counts.isError ? (
						<div className="mt-[1rem]">
							<EmptyState
								title={t("errors.countsTitle")}
								body={errorMessage(counts.error)}
								action={
									<Button variant="outline" onClick={() => void counts.refetch()}>
										{t("errors.retry")}
									</Button>
								}
							/>
						</div>
					) : (
						<KpiTiles totals={totals} />
					)}
					<QuickAccess session={session} totals={totals} />
					{games && (
						<div className="mt-[1.5rem]">
							{isControlRole(session.active_role) ? (
								<MissionControl key={games.games_id} games={games} canWrite={!readOnly} />
							) : (
								<GamesWork key={games.games_id} games={games} totals={totals} />
							)}
						</div>
					)}
				</>
			)}

			<FooterBand />
		</div>
	);
}

/** An operator's games at work: the progress strip, the live and unofficial lists, the sports, the tally. */
function GamesWork({ games, totals }: { games: CurrentGamesOption; totals: ReturnType<typeof groupTotals> | undefined }) {
	const { t } = useTranslation("dashboard");
	const gamesId = games.games_id;
	return (
		<div className="@container">
			<div className="grid grid-cols-1 gap-[1rem] @[56rem]:grid-cols-12">
				{totals && (
					<div className="@[56rem]:col-span-12">
						{totals.total === 0 ? <EmptyState title={t("progress.noneTitle")} body={t("progress.noneBody")} /> : <ProgressStrip totals={totals} />}
					</div>
				)}

				<MeetRowsCard
					className="@[56rem]:col-span-7"
					gamesId={gamesId}
					group="live"
					title={t("live.title")}
					allLabel={t("live.all")}
					emptyText={t("live.empty")}
				/>
				<MeetRowsCard
					className="@[56rem]:col-span-5"
					gamesId={gamesId}
					group="unofficial"
					title={t("unofficial.title")}
					allLabel={
						totals && totals.byGroup.unofficial > 0
							? t("unofficial.allCount", { count: totals.byGroup.unofficial, n: formatInteger(totals.byGroup.unofficial) })
							: t("unofficial.all")
					}
					emptyText={t("unofficial.empty")}
				/>

				<BySportCard className="@[56rem]:col-span-8" gamesId={gamesId} />
				<TallyCard className="@[56rem]:col-span-4 self-start" gamesId={gamesId} />
			</div>
		</div>
	);
}
